import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { posAPI, authAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { FaArrowLeft, FaSignOutAlt, FaStore, FaSearch, FaRedo } from 'react-icons/fa';

const STATUSES = ['', 'success', 'failed', 'pending', 'initiated'];

export default function POSTransactions() {
  const { logout } = useAuth();
  const navigate = useNavigate();

  const [rows, setRows]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const [status, setStatus]     = useState('');
  const [page, setPage]         = useState(1);
  const [meta, setMeta]         = useState(null);

  const fetch = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const r = await posAPI.transactions({ page: p, search: search || undefined, status: status || undefined });
      const d = r.data;
      setRows(d.data || d.transactions || []);
      setMeta(d.meta || null);
    } catch (_) {}
    finally { setLoading(false); }
  }, [search, status]);

  useEffect(() => { fetch(1); setPage(1); }, [search, status]);

  const handleLogout = async () => {
    try { await authAPI.logout(); } catch (_) {}
    logout();
    navigate('/login', { replace: true });
  };

  return (
    <div style={S.page}>
      <nav style={S.nav}>
        <div style={S.navBrand}><FaStore style={{ marginRight: 8 }} />A1xPay POS</div>
        <div style={S.navRight}>
          <button onClick={() => navigate('/pos/dashboard')} style={S.navBtn}><FaArrowLeft /></button>
          <button onClick={handleLogout} style={S.navBtn}><FaSignOutAlt /></button>
        </div>
      </nav>

      <div style={S.body}>
        <div style={S.topRow}>
          <div>
            <h1 style={S.heading}>Transaction History</h1>
            <p style={S.sub}>All POS payments collected through your terminal.</p>
          </div>
          <button onClick={() => { navigate('/pos/collect'); }} style={S.collectBtn}>+ Collect Payment</button>
        </div>

        {/* Filters */}
        <div style={S.filters}>
          <div style={S.searchWrap}>
            <FaSearch style={S.searchIcon} />
            <input
              value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search by transaction ID…" style={S.searchInput}
            />
          </div>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {STATUSES.map(s => (
              <button key={s} onClick={() => setStatus(s)}
                style={{ height: 36, padding: '0 12px', borderRadius: 999, border: `1.5px solid ${status === s ? '#7c3aed' : '#e8e8f1'}`, background: status === s ? '#7c3aed' : 'transparent', color: status === s ? '#fff' : '#6e6e86', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', transition: 'all .15s' }}>
                {s ? s.charAt(0).toUpperCase() + s.slice(1) : 'All'}
              </button>
            ))}
          </div>
          <button onClick={() => fetch(page)} style={S.refreshBtn}><FaRedo /></button>
        </div>

        {/* Table */}
        <div style={S.tableWrap}>
          {loading ? (
            <div style={S.empty}>Loading…</div>
          ) : rows.length === 0 ? (
            <div style={S.empty}>No transactions found.</div>
          ) : (
            <table style={S.table}>
              <thead>
                <tr>{['Transaction ID', 'Amount', 'Status', 'Payment Type', 'Date'].map(h => <th key={h} style={S.th}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {rows.map(t => (
                  <tr key={t.id} style={S.tr}>
                    <td style={S.td}><span style={{ fontFamily: 'monospace', fontSize: 12 }}>{t.a1x_transaction_id}</span></td>
                    <td style={{ ...S.td, fontWeight: 600 }}>₹{(t.amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                    <td style={S.td}><StatusBadge status={t.status} /></td>
                    <td style={S.td}>{t.payment_type || '—'}</td>
                    <td style={S.td}>{new Date(t.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true })}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {meta && meta.last_page > 1 && (
          <div style={S.pagRow}>
            <button disabled={page <= 1} onClick={() => { setPage(p => p - 1); fetch(page - 1); }} style={{ ...S.pagBtn, opacity: page <= 1 ? 0.4 : 1 }}>← Prev</button>
            <span style={{ fontSize: 13, color: '#4a4a6a' }}>Page {page} of {meta.last_page}</span>
            <button disabled={page >= meta.last_page} onClick={() => { setPage(p => p + 1); fetch(page + 1); }} style={{ ...S.pagBtn, opacity: page >= meta.last_page ? 0.4 : 1 }}>Next →</button>
          </div>
        )}
      </div>
    </div>
  );
}

const StatusBadge = ({ status }) => {
  const map = {
    success:   { bg: '#F0FDF4', color: '#16A34A', label: 'Success' },
    failed:    { bg: '#FEF2F2', color: '#DC2626', label: 'Failed' },
    pending:   { bg: '#FFFBEB', color: '#D97706', label: 'Pending' },
    initiated: { bg: '#f1ebff', color: '#7c3aed', label: 'Initiated' },
  };
  const s = map[status] || map.pending;
  return <span style={{ background: s.bg, color: s.color, padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600 }}>{s.label}</span>;
};

const S = {
  page:  { minHeight: '100vh', background: '#f9f9fc', fontFamily: '"Plus Jakarta Sans", system-ui, Arial, sans-serif' },
  nav:   { background: '#14142b', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 24px' },
  navBrand: { display: 'flex', alignItems: 'center', fontWeight: 700, fontSize: 18 },
  navRight: { display: 'flex', alignItems: 'center', gap: 8 },
  navBtn:   { background: 'none', border: '1px solid #334155', color: '#6e6e86', borderRadius: 8, padding: '6px 10px', cursor: 'pointer' },
  body:  { maxWidth: 1000, margin: '0 auto', padding: '32px 16px' },
  topRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, flexWrap: 'wrap', gap: 12 },
  heading: { fontSize: 24, fontWeight: 700, color: '#14142b', margin: '0 0 4px' },
  sub:     { fontSize: 13, color: '#4a4a6a', margin: 0 },
  collectBtn: { background: '#14142b', color: '#fff', border: 0, borderRadius: 10, padding: '10px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  filters: { display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' },
  searchWrap: { position: 'relative', flex: 1, minWidth: 200 },
  searchIcon: { position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#6e6e86', pointerEvents: 'none' },
  searchInput: { width: '100%', padding: '10px 14px 10px 36px', border: '1.5px solid #e8e8f1', borderRadius: 10, fontSize: 13, outline: 'none', boxSizing: 'border-box', color: '#14142b', background: '#fff' },
  select: { padding: '10px 14px', border: '1.5px solid #e8e8f1', borderRadius: 10, fontSize: 13, outline: 'none', color: '#14142b', background: '#fff', cursor: 'pointer' },
  refreshBtn: { background: '#fff', border: '1.5px solid #e8e8f1', borderRadius: 10, padding: '10px 14px', cursor: 'pointer', color: '#4a4a6a', fontSize: 14 },
  tableWrap: { background: '#fff', borderRadius: 14, boxShadow: '0 1px 4px rgba(0,0,0,.07)', overflow: 'hidden' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#4a4a6a', padding: '12px 16px', background: '#f4f4f9', borderBottom: '1px solid #e8e8f1', textTransform: 'uppercase', letterSpacing: '.04em' },
  td: { padding: '12px 16px', fontSize: 13, color: '#14142b', borderBottom: '1px solid #f0f0f6' },
  tr: {},
  empty: { padding: 48, textAlign: 'center', color: '#6e6e86', fontSize: 14 },
  pagRow: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, marginTop: 20 },
  pagBtn: { background: '#fff', border: '1.5px solid #e8e8f1', borderRadius: 8, padding: '8px 16px', fontSize: 13, cursor: 'pointer', color: '#14142b' },
};
